import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Archive, Eye, Loader2, Calendar, MapPin, Users, Trophy,
  X, FileJson, FileSpreadsheet, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminArchivePage = () => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewData, setViewData] = useState(null);

  useEffect(() => {
    loadArchives();
  }, []);

  const loadArchives = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/archives`, { withCredentials: true });
      setArchives(res.data.archives || []);
    } catch (e) {
      setArchives([]);
    } finally {
      setLoading(false);
    }
  };

  const viewArchive = async (id) => {
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/archives/${id}`, { withCredentials: true });
      setViewData(res.data);
    } catch (e) {
      setError('Failed to load archive details');
    }
  };

  const exportJSON = async (id, name) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/archives/${id}/export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'competition').replace(/\s+/g, '_')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export archive');
    }
  };

  const exportCSV = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/archives/${id}/export-csv`, {
        withCredentials: true, responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'results.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export CSV');
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="admin-archive-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Archive
        </h1>
        <p className="text-sm text-[#64748b] mt-1">Past competitions with final rankings — viewable and exportable.</p>
      </div>

      {error && (
        <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 rounded-2xl mb-4 text-sm font-medium" data-testid="archive-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#2565eb] animate-spin" />
        </div>
      ) : archives.length === 0 ? (
        <div className="card-olympiad p-12 text-center" data-testid="archive-empty-state">
          <Archive className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
          <p className="text-lg font-semibold text-[#64748b]">No archived competitions yet</p>
          <p className="text-sm text-[#94a3b8] mt-1">Deleted / completed competitions will appear here with full rankings preserved.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {archives.map((arch) => (
            <div key={arch.id} className="card-olympiad p-5" data-testid={`archive-card-${arch.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#0f172a] text-lg" style={{ fontFamily: 'Fredoka, sans-serif' }}>{arch.name}</p>
                  <div className="flex items-center gap-4 flex-wrap mt-2 text-sm text-[#64748b]">
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {arch.eventDate || '—'}</span>
                    {arch.venue && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {arch.venue}</span>}
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {arch.participants} participants</span>
                    {arch.ageGroup && <span className="px-2 py-0.5 rounded-full bg-[#eff6ff] text-[#2565eb] text-xs font-bold">Ages {arch.ageGroup}</span>}
                    {arch.has_rankings && <span className="flex items-center gap-1 text-[#16a34a] font-semibold"><Trophy className="w-4 h-4" /> Rankings available</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => viewArchive(arch.id)}
                    className="px-3 py-2 rounded-xl text-[#2565eb] hover:bg-[#eff6ff] transition-colors text-sm font-bold flex items-center gap-1"
                    data-testid={`view-archive-${arch.id}`}
                  >
                    <Eye className="w-4 h-4" /> View
                  </button>
                  <button
                    onClick={() => exportCSV(arch.id)}
                    className="px-3 py-2 rounded-xl text-[#16a34a] hover:bg-[#dcfce7] transition-colors text-sm font-bold flex items-center gap-1"
                    data-testid={`export-csv-${arch.id}`}
                  >
                    <FileSpreadsheet className="w-4 h-4" /> CSV
                  </button>
                  <button
                    onClick={() => exportJSON(arch.id, arch.name)}
                    className="px-3 py-2 rounded-xl text-[#64748b] hover:bg-[#f1f5f9] transition-colors text-sm font-bold flex items-center gap-1"
                    data-testid={`export-json-${arch.id}`}
                  >
                    <FileJson className="w-4 h-4" /> JSON
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Modal */}
      {viewData && (
        <ArchiveDetailModal data={viewData} onClose={() => setViewData(null)} onExportCSV={() => exportCSV(viewData.id)} onExportJSON={() => exportJSON(viewData.id, viewData.name)} />
      )}
    </div>
  );
};

const ArchiveDetailModal = ({ data, onClose, onExportCSV, onExportJSON }) => {
  const snap = data.snapshot || {};
  const rankings = snap.rankings || [];
  const participants = snap.participants || [];

  // Helper: get a flat view of a participant regardless of whether the snapshot
  // stored the nested (child.full_name) shape or the legacy flat shape.
  const flatPart = (p) => ({
    participant_id: p.participant_id,
    name: p?.child?.full_name || p.child_name || '—',
    age: p?.child?.age || p.child_age || null,
    school: p?.child?.school || p.child_school || '',
    city: p?.child?.city || p.child_city || '',
    parent_email: p?.parent?.email || p.parent_email || '',
    final_rank: p.final_rank,
  });

  // Build participant_id -> details map (both shapes supported)
  const pMap = {};
  for (const p of participants) {
    if (p.participant_id) pMap[p.participant_id] = flatPart(p);
  }

  const openCertificate = (pid) => {
    if (!pid) return;
    // Opens in new tab — the certificate page auto-downloads the PDF
    window.open(`/certificate?participant_id=${pid}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="archive-detail-modal">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#e2e8f0] px-6 py-4 flex items-start justify-between rounded-t-3xl">
          <div>
            <h2 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{data.name}</h2>
            <div className="flex items-center gap-4 flex-wrap mt-1.5 text-xs text-[#64748b]">
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {data.eventDate || '—'} {data.eventStartTime || ''}</span>
              {data.venue && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {data.venue}</span>}
              {data.ageGroup && <span className="px-2 py-0.5 rounded-full bg-[#eff6ff] text-[#2565eb] font-bold">Ages {data.ageGroup}</span>}
              {typeof data.participationFee !== 'undefined' && <span>Fee: ${data.participationFee}</span>}
              {data.archived_at && <span>Archived: {new Date(data.archived_at).toLocaleString()}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#f1f5f9]" data-testid="close-archive-modal">
            <X className="w-5 h-5 text-[#64748b]" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Stat row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Participants" value={snap.total_participants ?? participants.length ?? 0} />
            <StatCard label="Ranked" value={rankings.length} />
            <StatCard label="Top Score" value={rankings[0]?.total_points ?? '—'} />
            <StatCard label="Announcements" value={(snap.announcements || []).length} />
          </div>

          {/* Rankings */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                <Trophy className="w-5 h-5 text-[#f59e0b]" /> Final Rankings
              </h3>
              <div className="flex gap-2">
                <button onClick={onExportCSV} className="text-xs font-bold text-[#16a34a] hover:underline flex items-center gap-1" data-testid="modal-export-csv">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
                </button>
                <button onClick={onExportJSON} className="text-xs font-bold text-[#64748b] hover:underline flex items-center gap-1" data-testid="modal-export-json">
                  <FileJson className="w-3.5 h-3.5" /> JSON
                </button>
              </div>
            </div>
            {rankings.length === 0 ? (
              <div className="bg-[#f8fafc] rounded-2xl p-6 text-center text-sm text-[#64748b]">
                No rankings recorded for this competition. (Event may not have been started or completed.)
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-[#e2e8f0]">
                <table className="w-full text-sm">
                  <thead className="bg-[#f8fafc] text-[#475569]">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-bold">#</th>
                      <th className="px-4 py-2.5 text-left font-bold">Child</th>
                      <th className="px-4 py-2.5 text-left font-bold">School</th>
                      <th className="px-4 py-2.5 text-right font-bold">Points</th>
                      <th className="px-4 py-2.5 text-right font-bold">Accuracy</th>
                      <th className="px-4 py-2.5 text-center font-bold">Certificate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map((r) => {
                      const p = pMap[r.participant_id] || {};
                      const rank = r.rank;
                      const rankColor =
                        rank === 1 ? 'bg-[#fef3c7] text-[#92400e]' :
                        rank === 2 ? 'bg-[#e5e7eb] text-[#374151]' :
                        rank === 3 ? 'bg-[#fed7aa] text-[#9a3412]' :
                        'bg-[#f1f5f9] text-[#475569]';
                      // Prefer registration name → display_name → name fallback
                      const childName = p.name && p.name !== '—' ? p.name : (r.display_name || r.name || '—');
                      return (
                        <tr key={r.participant_id} className="border-t border-[#e2e8f0]" data-testid={`rank-row-${r.participant_id}`}>
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${rankColor}`}>
                              {rank}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-semibold text-[#0f172a]">
                            {childName}
                            {p.age && <span className="text-[#94a3b8] font-normal ml-1">({p.age})</span>}
                          </td>
                          <td className="px-4 py-2.5 text-[#64748b]">{p.school || '—'}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-[#0f172a]">{r.total_points ?? '—'}</td>
                          <td className="px-4 py-2.5 text-right text-[#64748b]">{r.accuracy != null ? `${r.accuracy}%` : '—'}</td>
                          <td className="px-4 py-2.5 text-center">
                            <button
                              onClick={() => openCertificate(r.participant_id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-[#2565eb] hover:bg-[#eff6ff] transition-colors"
                              data-testid={`download-cert-${r.participant_id}`}
                              title={rank <= 3 ? 'Download winner certificate' : 'Download participation certificate'}
                            >
                              <Award className="w-3.5 h-3.5" />
                              {rank <= 3 ? 'Winner' : 'Participation'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Participants without final rank (paid but didn't finish) */}
          {participants.filter(p => p.final_rank == null).length > 0 && (
            <div>
              <h3 className="font-bold text-[#0f172a] mb-2 text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Registered (no final rank recorded)
              </h3>
              <div className="bg-[#f8fafc] rounded-2xl border border-[#e2e8f0] divide-y divide-[#e2e8f0]">
                {participants.filter(p => p.final_rank == null).map((p, i) => {
                  const f = flatPart(p);
                  return (
                    <div key={f.participant_id || i} className="px-4 py-2 text-sm flex items-center justify-between">
                      <span className="font-semibold text-[#0f172a]">{f.name} {f.age && <span className="text-[#94a3b8] font-normal">({f.age})</span>}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-[#64748b] text-xs">{f.school || '—'} · {f.parent_email || '—'}</span>
                        <button
                          onClick={() => openCertificate(f.participant_id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-[#2565eb] hover:bg-[#eff6ff]"
                          data-testid={`download-cert-${f.participant_id}`}
                        >
                          <Award className="w-3 h-3" /> Participation
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-[#f8fafc] rounded-2xl p-4 text-center border border-[#e2e8f0]">
    <p className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{value}</p>
    <p className="text-xs font-semibold text-[#64748b] mt-1">{label}</p>
  </div>
);

export default AdminArchivePage;
